import { ref, computed, type Ref } from "vue";
import * as Cesium from "cesium";

export interface FlyModeState {
  isFlyMode: boolean;
  pressedKeys: Set<string>;
  shiftPressed: boolean;
  isMouseLooking: boolean;
  lastFrameTimeMs: number | null;
  inputHandler: Cesium.ScreenSpaceEventHandler | null;
  onPreRender: ((scene: Cesium.Scene, time: Cesium.JulianDate) => void) | null;
  savedControllerState: {
    enableRotate: boolean;
    enableTranslate: boolean;
    enableTilt: boolean;
    enableLook: boolean;
    enableZoom: boolean;
  } | null;
}

export function useFlyMode(viewer: any) {
  const isFlyMode = ref<boolean>(false);
  
  // Fly mode internals
  const pressedKeys = new Set<string>();
  let shiftPressed = false;
  let isMouseLooking = false;
  let lastFrameTimeMs: number | null = null;
  let onPreRender: ((scene: Cesium.Scene, time: Cesium.JulianDate) => void) | null = null;
  let inputHandler: Cesium.ScreenSpaceEventHandler | null = null;
  let savedControllerState: {
    enableRotate: boolean;
    enableTranslate: boolean;
    enableTilt: boolean;
    enableLook: boolean;
    enableZoom: boolean;
  } | null = null;


  const keepCameraUpright = (): void => {
    if (!viewer.value) return;
    const camera = viewer.value.camera;
    const ellipsoid = viewer.value.scene.globe.ellipsoid;
    const worldUp = ellipsoid.geodeticSurfaceNormal(
      camera.position,
      new Cesium.Cartesian3()
    );
    const direction = Cesium.Cartesian3.normalize(
      camera.direction,
      new Cesium.Cartesian3()
    );
    let right = Cesium.Cartesian3.cross(
      direction,
      worldUp,
      new Cesium.Cartesian3()
    );
    const rightMag = Cesium.Cartesian3.magnitude(right);
    if (rightMag < 1e-6) {
      // Direction is near parallel to worldUp; skip adjustment to avoid jitter
      return;
    }
    right = Cesium.Cartesian3.multiplyByScalar(
      right,
      1 / rightMag,
      right
    );
    const up = Cesium.Cartesian3.normalize(
      Cesium.Cartesian3.cross(right, direction, new Cesium.Cartesian3()),
      new Cesium.Cartesian3()
    );
    camera.right = right;
    camera.up = up;
  };

  const applyYawPitch = (yaw: number, pitch: number): void => {
    if (!viewer.value) return;
    const camera = viewer.value.camera;
    const ellipsoid = viewer.value.scene.globe.ellipsoid;
    const worldUp = ellipsoid.geodeticSurfaceNormal(
      camera.position,
      new Cesium.Cartesian3()
    );

    // Start from current direction normalized
    let direction = Cesium.Cartesian3.normalize(
      camera.direction,
      new Cesium.Cartesian3()
    );

    // Apply yaw around world up (prevents unintended pitch/roll while panning left/right)
    if (yaw !== 0) {
      const qYaw = Cesium.Quaternion.fromAxisAngle(worldUp, yaw);
      const mYaw = Cesium.Matrix3.fromQuaternion(qYaw);
      direction = Cesium.Matrix3.multiplyByVector(
        mYaw,
        direction,
        new Cesium.Cartesian3()
      );
      direction = Cesium.Cartesian3.normalize(direction, direction);
    }

    // Recompute right after yaw
    let right = Cesium.Cartesian3.normalize(
      Cesium.Cartesian3.cross(direction, worldUp, new Cesium.Cartesian3()),
      new Cesium.Cartesian3()
    );

    // Apply pitch around the camera's right axis
    if (pitch !== 0) {
      const qPitch = Cesium.Quaternion.fromAxisAngle(right, pitch);
      const mPitch = Cesium.Matrix3.fromQuaternion(qPitch);
      direction = Cesium.Matrix3.multiplyByVector(
        mPitch,
        direction,
        new Cesium.Cartesian3()
      );
      direction = Cesium.Cartesian3.normalize(direction, direction);
    }

    // Clamp pitch to avoid flipping straight up/down
    const dotUp = Cesium.Cartesian3.dot(direction, worldUp);
    const maxDot = 0.995; // ~5.7 degrees from vertical
    if (Math.abs(dotUp) > maxDot) {
      const clampedDot = (dotUp > 0 ? 1 : -1) * maxDot;
      const vertical = Cesium.Cartesian3.multiplyByScalar(
        worldUp,
        dotUp,
        new Cesium.Cartesian3()
      );
      const horizontal = Cesium.Cartesian3.normalize(
        Cesium.Cartesian3.subtract(direction, vertical, new Cesium.Cartesian3()),
        new Cesium.Cartesian3()
      );
      const horizontalScale = Math.sqrt(1 - clampedDot * clampedDot);
      const newHorizontal = Cesium.Cartesian3.multiplyByScalar(
        horizontal,
        horizontalScale,
        new Cesium.Cartesian3()
      );
      const newVertical = Cesium.Cartesian3.multiplyByScalar(
        worldUp,
        clampedDot,
        new Cesium.Cartesian3()
      );
      direction = Cesium.Cartesian3.normalize(
        Cesium.Cartesian3.add(
          newHorizontal,
          newVertical,
          new Cesium.Cartesian3()
        ),
        new Cesium.Cartesian3()
      );
    }

    // Rebuild orthonormal basis (eliminate roll)
    right = Cesium.Cartesian3.normalize(
      Cesium.Cartesian3.cross(direction, worldUp, new Cesium.Cartesian3()),
      new Cesium.Cartesian3()
    );
    const up = Cesium.Cartesian3.normalize(
      Cesium.Cartesian3.cross(right, direction, new Cesium.Cartesian3()),
      new Cesium.Cartesian3()
    );

    camera.direction = direction;
    camera.right = right;
    camera.up = up;
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!isFlyMode.value) return;
    const key = e.key.toLowerCase();
    if (key === "shift") {
      shiftPressed = true;
    }
    if (key === "w" || key === "a" || key === "s" || key === "d") {
      pressedKeys.add(key);
      e.preventDefault();
    }
  };

  const handleKeyUp = (e: KeyboardEvent) => {
    if (!isFlyMode.value) return;
    const key = e.key.toLowerCase();
    if (key === "shift") {
      shiftPressed = false;
    }
    if (key === "w" || key === "a" || key === "s" || key === "d") {
      pressedKeys.delete(key);
      e.preventDefault();
    }
  };

  const enableFlyMode = (): void => {
    if (!viewer.value || isFlyMode.value) return;

    isFlyMode.value = true;

    // Save and adjust camera controller settings
    const controller = viewer.value.scene.screenSpaceCameraController;
    savedControllerState = {
      enableRotate: controller.enableRotate,
      enableTranslate: controller.enableTranslate,
      enableTilt: controller.enableTilt,
      enableLook: controller.enableLook,
      enableZoom: controller.enableZoom,
    };
    controller.enableRotate = false;
    controller.enableTranslate = false;
    controller.enableTilt = false;
    controller.enableLook = false; // disable Cesium's look to avoid conflicts with our FPS look
    controller.enableZoom = true;

    // Keyboard listeners
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    // Mouse look handlers
    inputHandler = new Cesium.ScreenSpaceEventHandler(viewer.value.canvas);
    inputHandler.setInputAction(() => {
      isMouseLooking = true;
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);
    inputHandler.setInputAction(() => {
      isMouseLooking = false;
    }, Cesium.ScreenSpaceEventType.LEFT_UP);
    inputHandler.setInputAction((movement: any) => {
      if (!isMouseLooking || !viewer.value) return;
      const dx = movement.endPosition.x - movement.startPosition.x;
      const dy = movement.endPosition.y - movement.startPosition.y;
      const sensitivity = 0.003; // radians per pixel
      const yaw = -dx * sensitivity; // invert so panning left looks left
      const pitch = -dy * sensitivity; // natural vertical look
      applyYawPitch(yaw, pitch);
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    // Per-frame movement update
    lastFrameTimeMs = null;
    onPreRender = (scene: Cesium.Scene) => {
      if (!viewer.value) return;
      const now = performance.now();
      const dt = lastFrameTimeMs == null ? 0 : (now - lastFrameTimeMs) / 1000.0;
      lastFrameTimeMs = now;
      if (dt === 0) return;

      const camera = viewer.value.camera;
      const ellipsoid = viewer.value.scene.globe.ellipsoid;
      const carto = ellipsoid.cartesianToCartographic(camera.position);
      const height = Math.max(carto ? carto.height : 0, 1);
      // Base speed scales with altitude; Shift increases speed
      const baseSpeed = Math.max(height * 0.3, 25); // meters per second
      const speed = baseSpeed * dt * (shiftPressed ? 6 : 1); // increased shift multiplier from 4 to 6

      if (pressedKeys.has("w")) {
        camera.moveForward(speed);
      }
      if (pressedKeys.has("s")) {
        camera.moveBackward(speed);
      }
      if (pressedKeys.has("a")) {
        camera.moveLeft(speed);
      }
      if (pressedKeys.has("d")) {
        camera.moveRight(speed);
      }
      keepCameraUpright();
    };
    viewer.value.scene.preRender.addEventListener(onPreRender);
  };

  const disableFlyMode = (): void => {
    if (!viewer.value || !isFlyMode.value) return;
    isFlyMode.value = false;

    // Restore controller settings
    const controller = viewer.value.scene.screenSpaceCameraController;
    if (savedControllerState) {
      controller.enableRotate = savedControllerState.enableRotate;
      controller.enableTranslate = savedControllerState.enableTranslate;
      controller.enableTilt = savedControllerState.enableTilt;
      controller.enableLook = savedControllerState.enableLook;
      controller.enableZoom = savedControllerState.enableZoom;
    }
    savedControllerState = null;

    // Remove keyboard listeners
    window.removeEventListener("keydown", handleKeyDown);
    window.removeEventListener("keyup", handleKeyUp);
    pressedKeys.clear();
    shiftPressed = false;

    // Remove mouse handlers
    if (inputHandler) {
      inputHandler.destroy();
      inputHandler = null;
    }
    isMouseLooking = false;

    // Remove per-frame update
    if (onPreRender) {
      viewer.value.scene.preRender.removeEventListener(onPreRender);
      onPreRender = null;
    }
    lastFrameTimeMs = null;
  };

  const toggleFlyMode = (): void => {
    if (isFlyMode.value) {
      disableFlyMode();
    } else {
      enableFlyMode();
    }
  };

  const cleanup = (): void => {
    if (isFlyMode.value) {
      disableFlyMode();
    }
  };

  return {
    isFlyMode,
    toggleFlyMode,
    enableFlyMode,
    disableFlyMode,
    cleanup,
  };
}
